import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";
import WhiteLogo from "../assets/images/whitelogo.png";
import CatchupClass, { CatchupRequestModal } from "./CatchupClass";
import PackageQrCard from "./PackageQrCard";
import "@fontsource-variable/plus-jakarta-sans";
import "../style/studentPortal.css";

const VIEWS = [
  { key: "dashboard", label: "Dashboard", icon: "bi-house-door" },
  { key: "classes", label: "My Classes", icon: "bi-journal-text" },
  { key: "catchup", label: "Catchup Class", icon: "bi-play-btn" },
  { key: "profile", label: "Profile", icon: "bi-person" },
];

// Kolom Status menampilkan hasil kelas menurut KEHADIRAN (Passed / Failed),
// bukan sekadar selesai atau belum. Hasil itu baru ada untuk kelas yang sudah
// selesai dan absensinya dicatat; selain itu yang ditampilkan adalah alasan
// kenapa hasilnya belum ada.
const statusBadge = (c) => {
  if (c.onLeave) return { label: "On leave", className: "is-muted" };
  if (c.attendance.result === "passed") return { label: "Passed", className: "is-passed" };
  if (c.attendance.result === "failed") return { label: "Failed", className: "is-failed" };
  if (c.phase === "ongoing") return { label: "In progress", className: "is-ongoing" };
  if (c.phase === "upcoming") return { label: "Upcoming", className: "is-upcoming" };
  if (c.phase === "completed") return { label: "Not recorded", className: "is-muted" };
  return { label: "Not scheduled", className: "is-muted" };
};

// Tanggal dari server berbentuk "YYYY-MM-DD" (tanpa jam). Dibaca sebagai UTC
// dan diformat sebagai UTC supaya tidak bergeser sehari di zona waktu mana pun.
const toDate = (iso) => new Date(`${iso}T00:00:00Z`);
const fmt = (iso, options) => new Intl.DateTimeFormat("en-GB", { timeZone: "UTC", ...options }).format(toDate(iso));
const longDate = (iso) => (iso ? fmt(iso, { day: "numeric", month: "long", year: "numeric" }) : "");
const shortDate = (iso) => (iso ? fmt(iso, { day: "numeric", month: "short" }) : "");

// "14 – 18 September 2026"; bulan/tahun awal hanya ditulis kalau berbeda.
const dateRange = (start, end) => {
  if (!start) return "";
  if (!end || start === end) return longDate(start);
  const [sy, sm] = start.split("-");
  const [ey, em] = end.split("-");
  if (sy !== ey) return `${longDate(start)} – ${longDate(end)}`;
  if (sm !== em) return `${fmt(start, { day: "numeric", month: "long" })} – ${longDate(end)}`;
  return `${fmt(start, { day: "numeric" })} – ${longDate(end)}`;
};

const daysBetween = (fromIso, toIso) => Math.round((toDate(toIso) - toDate(fromIso)) / 86400000);

const scoreText = (score) => (score === null || score === undefined ? null : String(Math.round(score * 100) / 100));

const AttendanceCell = ({ attendance }) => {
  if (attendance.totalSessions === 0 || attendance.percent === null) return <span className="sp-dim">Not recorded</span>;
  return (
    <>
      {attendance.present}/{attendance.counted} sessions <span className="sp-dim">· {attendance.percent}%</span>
    </>
  );
};

const AssignmentCell = ({ assignments }) => {
  if (assignments.finalScore !== null) return <strong>{scoreText(assignments.finalScore)}</strong>;
  if (assignments.items.length === 0) return <span className="sp-dim">—</span>;
  const graded = assignments.items.filter((i) => i.status === "graded").length;
  return (
    <span className="sp-dim">
      {graded}/{assignments.items.length} graded
    </span>
  );
};

const ATTENDANCE_MARKS = {
  present: { icon: "bi-check-lg", label: "Present", className: "is-present" },
  // Hadir lewat video kelas susulan ("Hadir Susulan" di sisi admin) —
  // dihitung sama dengan hadir biasa.
  catchup: { icon: "bi-play-fill", label: "In progress (Catchup Class)", className: "is-catchup" },
  absent: { icon: "bi-x-lg", label: "Absent", className: "is-absent" },
  none: { icon: "bi-dash", label: "Not recorded", className: "is-none" },
};

const ASSIGNMENT_STATES = {
  submitted: { label: "Awaiting grade", className: "is-upcoming" },
  not_submitted: { label: "Not submitted", className: "is-muted" },
};

const RESOURCE_ICONS = { pdf: "bi-file-earmark-pdf", video: "bi-play-circle", file: "bi-file-earmark" };

// Student Resource dan video Q&A kelas, diambil dari Google Classroom. Baru
// dimuat saat rincian kelas dibuka, dan kartunya tidak ditampilkan sama
// sekali kalau kelasnya belum punya materi (atau belum ditautkan ke
// Classroom) — kartu kosong di setiap kelas lama hanya menambah keramaian.
const ClassResources = ({ code, batch }) => {
  const [state, setState] = useState({ status: "loading", items: [] });

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`/studentportal/resources/${encodeURIComponent(code)}/${encodeURIComponent(batch)}`)
      .then((res) => {
        if (!cancelled) setState({ status: "ready", items: res.data.items || [] });
      })
      .catch(() => {
        if (!cancelled) setState({ status: "error", items: [] });
      });
    return () => {
      cancelled = true;
    };
  }, [code, batch]);

  if (state.status === "ready" && state.items.length === 0) return null;

  return (
    <section className="sp-panel sp-panel-wide">
      <h4>Student Resources</h4>
      {state.status === "loading" && <p className="sp-panel-empty">Loading resources...</p>}
      {state.status === "error" && (
        <p className="sp-panel-empty">Unable to load the class resources right now. Please try again later.</p>
      )}
      {state.items.length > 0 && (
        <>
          <div className="sp-res">
            {state.items.map((r) => (
              <a key={r.url} className="sp-res-item" href={r.url} target="_blank" rel="noopener noreferrer">
                <i className={`bi ${RESOURCE_ICONS[r.type] || RESOURCE_ICONS.file}`} aria-hidden="true"></i>
                <span className="sp-res-title">{r.title.replace(/.(pdf|mp4|mov|mkv|webm)$/i, "")}</span>
                {r.kind === "qna" && <span className="sp-badge is-upcoming">Q&amp;A</span>}
              </a>
            ))}
          </div>
          <p className="sp-res-note">
            Files open in Google Drive. Sign in with the Google account you use for Google Classroom.
          </p>
        </>
      )}
    </section>
  );
};

// Rincian satu kelas. Kehadiran disusun sebagai tabel kecil hari x sesi
// dengan satu ikon per sel — bukan sepuluh kotak yang masing-masing mengulang
// "Day 1 · S1 / tanggal / Present" — supaya polanya terbaca sekilas: kolom
// mana yang hadir, kolom mana yang tidak.
// Tabel kehadiran hari x sesi, dipakai di detail kelas dan di popup "Request a
// catchup class". Di popup (`pick`), sesi yang bisa diminta menjadi tombol yang
// mencentang/melepas sesi itu, sesi lain diredupkan.
//   pick = { selectable: Map("day|session" → videoId), picked: videoId[], onToggle(videoId) }
const AttendanceGrid = ({ sessions, pick }) => {
  const days = [];
  for (const s of sessions) {
    let day = days.find((d) => d.dayNumber === s.dayNumber);
    if (!day) {
      day = { dayNumber: s.dayNumber, date: s.date, bySession: {} };
      days.push(day);
    }
    day.bySession[s.sessionNumber] = s.viaMakeup ? "catchup" : s.status || "none";
  }
  days.sort((a, b) => a.dayNumber - b.dayNumber);
  const sessionNumbers = [...new Set(sessions.map((s) => s.sessionNumber))].sort((a, b) => a - b);

  return (
    <>
      <div className="sp-att-scroll">
        {/* Jumlah hari dioper sebagai variabel CSS, bukan
            grid-template-columns langsung: di layar sempit aturan
            kolomnya diganti dari stylesheet, dan inline style tidak
            bisa ditimpa dari sana. */}
        <div className={`sp-att${pick ? " is-pick" : ""}`} style={{ "--sp-days": days.length }}>
          <span></span>
          {days.map((d) => (
            <div key={d.dayNumber} className="sp-att-day">
              <b>Day {d.dayNumber}</b>
              {shortDate(d.date)}
            </div>
          ))}
          {sessionNumbers.map((n) => (
            <React.Fragment key={n}>
              <span className="sp-att-row">Session {n}</span>
              {days.map((d) => {
                const mark = ATTENDANCE_MARKS[d.bySession[n] || "none"];
                const videoId = pick ? pick.selectable.get(`${d.dayNumber}|${n}`) : undefined;
                const icon = (
                  <span className={`sp-mark ${mark.className}`} title={mark.label} aria-label={mark.label}>
                    <i className={`bi ${mark.icon}`} aria-hidden="true"></i>
                  </span>
                );
                if (videoId === undefined) {
                  return (
                    <div key={d.dayNumber} className={`sp-att-cell${pick ? " is-locked" : ""}`}>
                      {icon}
                    </div>
                  );
                }
                const on = pick.picked.includes(videoId);
                return (
                  <div key={d.dayNumber} className="sp-att-cell">
                    <button
                      type="button"
                      className={`sp-att-pick${on ? " is-on" : ""}`}
                      aria-pressed={on}
                      aria-label={`Day ${d.dayNumber} Session ${n}: ${mark.label}${on ? ", selected" : ""}`}
                      onClick={() => pick.onToggle(videoId)}
                    >
                      {icon}
                    </button>
                  </div>
                );
              })}
            </React.Fragment>
          ))}
        </div>
      </div>

      <div className="sp-legend">
        {Object.values(ATTENDANCE_MARKS).map((m) => (
          <span key={m.label}>
            <span className={`sp-mark is-small ${m.className}`}>
              <i className={`bi ${m.icon}`} aria-hidden="true"></i>
            </span>
            {m.label}
          </span>
        ))}
        {pick && (
          <span>
            <span className="sp-mark is-small is-absent sp-att-legend-on">
              <i className="bi bi-x-lg" aria-hidden="true"></i>
            </span>
            Selected
          </span>
        )}
      </div>
    </>
  );
};

// Panel Catchup Class di detail kelas: tempat student meminta kelas susulan
// untuk kelas ini, plus pintasan ke rekaman yang sudah disetujui. Tampil untuk
// kelas batch terbaru yang sudah dimulai (catchupOpen dari server), walaupun
// videonya belum diunggah — di popup, sesi seperti itu tertulis "Video not
// available". Kelas batch lama tidak menampilkannya.
const CatchupPanel = ({ c, onChanged }) => {
  const [modalOpen, setModalOpen] = useState(false);
  const closeModal = useCallback(() => setModalOpen(false), []);
  const available = c.catchupAvailable || 0;
  const pending = c.catchupPending || 0;
  if (c.onLeave || c.attendance.sessions.length === 0) return null;
  if (!c.catchupOpen && available === 0 && pending === 0) return null;

  return (
    <section className="sp-panel">
      <h4>Catchup Class</h4>
      {available > 0 && (
        <p className="sp-panel-empty">
          {available} approved recording{available === 1 ? " is" : "s are"} ready for you in this class.
        </p>
      )}
      {pending > 0 && (
        <p className="sp-panel-empty">
          {pending} request{pending === 1 ? " is" : "s are"} waiting for approval.
        </p>
      )}
      {available === 0 && pending === 0 && (
        <p className="sp-panel-empty">
          Missed a session? Request a catchup class to watch its recording and have your attendance updated.
        </p>
      )}
      <div className="sp-makeup">
        <button
          type="button"
          className={`sp-makeup-btn${available === 0 ? " is-primary" : ""}`}
          onClick={() => setModalOpen(true)}
        >
          <i className="bi bi-plus-circle" aria-hidden="true"></i>
          Request a catchup class
        </button>
        {(available > 0 || pending > 0) && (
          <Link className={`sp-makeup-btn${available > 0 ? " is-primary" : ""}`} to="/portal/catchup">
            <i className="bi bi-play-btn" aria-hidden="true"></i>
            {available > 0 ? "Watch recordings" : "See my requests"}
          </Link>
        )}
      </div>
      {modalOpen && (
        <CatchupRequestModal
          classCode={c.code}
          batch={c.batch}
          className={c.name}
          sessions={c.attendance.sessions}
          renderGrid={(pick) => <AttendanceGrid sessions={c.attendance.sessions} pick={pick} />}
          onClose={closeModal}
          onSent={() => {
            setModalOpen(false);
            onChanged();
          }}
        />
      )}
    </section>
  );
};

const ClassDetail = ({ c, onChanged }) => {
  const { attendance, assignments } = c;

  const recorded = attendance.percent !== null;

  return (
    <div className="sp-detail-grid">
      <section className="sp-panel">
        <h4>Attendance</h4>
        {attendance.sessions.length === 0 ? (
          <p className="sp-panel-empty">Sessions for this class have not been scheduled yet.</p>
        ) : (
          <>
            {recorded ? (
              <div className="sp-att-summary">
                <p>
                  <strong>
                    {attendance.present} of {attendance.counted}
                  </strong>{" "}
                  sessions attended
                </p>
                <div
                  className={`sp-bar ${attendance.result === "failed" ? "is-failed" : ""}`}
                  role="img"
                  aria-label={`${attendance.percent}% attendance`}
                >
                  <span style={{ width: `${attendance.percent}%` }}></span>
                </div>
              </div>
            ) : (
              <p className="sp-panel-empty">Attendance has not been recorded for this class yet.</p>
            )}

            <AttendanceGrid sessions={attendance.sessions} />
          </>
        )}
      </section>

      <div className="sp-detail-col">
      <section className="sp-panel">
        <h4>Assignments</h4>
        {assignments.items.length === 0 && assignments.finalScore === null ? (
          <p className="sp-panel-empty">No assignment results are available for this class.</p>
        ) : (
          <>
            {assignments.items.map((a, i) => (
              <div className="sp-asg" key={i}>
                <span className="sp-asg-title">{a.title}</span>
                {a.status === "graded" ? (
                  <strong className="sp-asg-score">
                    {scoreText(a.score)}
                    {a.letter ? <small>{a.letter}</small> : null}
                  </strong>
                ) : (
                  <span className={`sp-badge ${ASSIGNMENT_STATES[a.status].className}`}>
                    {ASSIGNMENT_STATES[a.status].label}
                  </span>
                )}
              </div>
            ))}
            {assignments.finalScore !== null && (
              <div className="sp-asg is-total">
                <span className="sp-asg-title">Class assignment score</span>
                <strong className="sp-asg-score">{scoreText(assignments.finalScore)}</strong>
              </div>
            )}
          </>
        )}
      </section>

      <CatchupPanel c={c} onChanged={onChanged} />
      </div>

      {c.batch !== null && <ClassResources code={c.code} batch={c.batch} />}
    </div>
  );
};

// Tombol bergabung ke Google Classroom / grup WhatsApp kelas yang sedang
// berjalan (link diisi admin, hanya dikirim backend untuk student kelas itu)
// + Excuse Form (selalu ada).
const JoinButtons = ({ links, excuseFormUrl }) => {
  const classroomUrl = links && links.classroomUrl;
  const whatsappUrl = links && links.whatsappUrl;
  if (!classroomUrl && !whatsappUrl && !excuseFormUrl) return null;
  return (
    <div className="sp-join">
      {classroomUrl && (
        <a className="sp-join-btn is-classroom" href={classroomUrl} target="_blank" rel="noopener noreferrer">
          <i className="bi bi-mortarboard-fill" aria-hidden="true"></i>
          Join Google Classroom
        </a>
      )}
      {whatsappUrl && (
        <a className="sp-join-btn is-whatsapp" href={whatsappUrl} target="_blank" rel="noopener noreferrer">
          <i className="bi bi-whatsapp" aria-hidden="true"></i>
          Join WhatsApp Group
        </a>
      )}
      {excuseFormUrl && (
        <a className="sp-join-btn is-excuse" href={excuseFormUrl} target="_blank" rel="noopener noreferrer">
          <i className="bi bi-envelope-paper" aria-hidden="true"></i>
          Excuse Form
        </a>
      )}
    </div>
  );
};

const NextClassCard = ({ nextClass, today, excuseFormUrl, joinLinks }) => {
  if (!nextClass) {
    return (
      <div className="sp-next is-empty">
        <p className="sp-next-label">Next class</p>
        <h2>No upcoming class</h2>
        <p className="sp-next-note">
          You don't have any class scheduled at the moment. Once you are enrolled in a new class and its schedule is
          confirmed, it will appear here.
        </p>
      </div>
    );
  }

  const inProgress = nextClass.phase === "ongoing";
  let countdown = null;
  if (!inProgress && nextClass.startDate) {
    const days = daysBetween(today, nextClass.startDate);
    countdown = days === 1 ? "Starts tomorrow" : `Starts in ${days} days`;
  }

  return (
    <div className="sp-next">
      <p className={`sp-next-label ${inProgress ? "is-live" : ""}`}>
        {inProgress && <span className="sp-live-dot" aria-hidden="true"></span>}
        {inProgress ? "Class in progress" : "Next class"}
      </p>
      <h2>{nextClass.name}</h2>
      <div className="sp-next-meta">
        <span>
          <i className="bi bi-calendar3"></i>
          {nextClass.startDate ? dateRange(nextClass.startDate, nextClass.endDate) : nextClass.dateText}
        </span>
        <span>
          <i className="bi bi-bookmark"></i>
          {nextClass.code}
          {nextClass.batch ? ` · Batch ${nextClass.batch}` : ""}
        </span>
        {countdown && (
          <span>
            <i className="bi bi-clock"></i>
            {countdown}
          </span>
        )}
      </div>
      {nextClass.tentative && (
        <p className="sp-next-note">
          Tentative date based on the academy schedule. The exact sessions will appear here once they are confirmed.
        </p>
      )}
      <JoinButtons links={joinLinks} excuseFormUrl={excuseFormUrl} />
    </div>
  );
};

const ClassTable = ({ classes, expandable, onChanged }) => {
  const [open, setOpen] = useState(null);

  if (classes.length === 0) return <p className="sp-dim" style={{ margin: 0 }}>No classes on record yet.</p>;

  return (
    <div className="sp-table-wrap">
      <table className="sp-table">
        <thead>
          <tr>
            <th>Class</th>
            <th className="sp-col-batch">Batch</th>
            <th>Date</th>
            <th>Status</th>
            <th>Attendance</th>
            <th>Assignment score</th>
            {expandable && <th></th>}
          </tr>
        </thead>
        <tbody>
          {classes.map((c) => {
            const id = `${c.code}|${c.batch}`;
            const badge = statusBadge(c);
            const isOpen = open === id;
            return (
              <React.Fragment key={id}>
                <tr className={expandable ? "sp-row" : undefined} onClick={expandable ? () => setOpen(isOpen ? null : id) : undefined}>
                  <td>
                    <span className="sp-class-name">{c.name}</span>
                    <span className="sp-class-code">{c.code}</span>
                  </td>
                  {/* data-label: di layar sempit tabel ini berubah menjadi kartu
                      dan judul kolomnya ditulis di depan tiap nilai (lihat
                      "Tabel menjadi kartu" di studentPortal.css). Isi sel
                      dibungkus satu <span> supaya tetap satu kesatuan. */}
                  <td className="sp-col-batch" data-label="Batch">
                    <span>{c.batch || <span className="sp-dim">—</span>}</span>
                  </td>
                  <td className="sp-nowrap" data-label="Date">
                    <span>{c.startDate ? dateRange(c.startDate, c.endDate) : <span className="sp-dim">—</span>}</span>
                  </td>
                  <td data-label="Status">
                    <span className={`sp-badge ${badge.className}`}>{badge.label}</span>
                  </td>
                  <td className="sp-nowrap" data-label="Attendance">
                    <span>
                      <AttendanceCell attendance={c.attendance} />
                    </span>
                  </td>
                  <td data-label="Assignment score">
                    <span>
                      <AssignmentCell assignments={c.assignments} />
                    </span>
                  </td>
                  {expandable && (
                    <td className="sp-td-action">
                      <span className="sp-td-more">{isOpen ? "Hide details" : "View details"}</span>
                      <i className={`bi ${isOpen ? "bi-chevron-up" : "bi-chevron-down"} sp-dim`}></i>
                    </td>
                  )}
                </tr>
                {expandable && isOpen && (
                  <tr className="sp-detail">
                    <td colSpan={7}>
                      <ClassDetail c={c} onChanged={onChanged} />
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const DashboardView = ({ data, goTo }) => {
  const { student, stats, nextClass, classes, today, excuseFormUrl, joinLinks } = data;
  // Kelas yang sudah punya tanggal, terbaru di atas — yang belum dijadwalkan
  // tidak memberi informasi apa pun di ringkasan.
  const recent = classes
    .filter((c) => c.startDate && c.startDate <= today)
    .slice(-4)
    .reverse();

  return (
    <>
      <NextClassCard nextClass={nextClass} today={today} excuseFormUrl={excuseFormUrl} joinLinks={joinLinks} />

      <div className="sp-stats">
        <div className="sp-stat">
          <span>Student ID</span>
          <strong>{student.studentId}</strong>
        </div>
        <div className="sp-stat">
          <span>Program</span>
          <strong>{student.program}</strong>
          {student.batchJoined && <small>Batch {student.batchJoined}</small>}
        </div>
        <div className="sp-stat">
          <span>Classes completed</span>
          <strong>
            {stats.completedClasses} of {stats.totalClasses}
          </strong>
        </div>
        <div className="sp-stat">
          <span>Attendance</span>
          <strong>{stats.attendancePercent === null ? "—" : `${stats.attendancePercent}%`}</strong>
          {stats.sessionsCounted > 0 && (
            <small>
              {stats.sessionsPresent} of {stats.sessionsCounted} sessions attended
            </small>
          )}
        </div>
      </div>

      <div className="sp-card">
        <p className="sp-card-title">
          Recent classes
          <button type="button" className="sp-link" onClick={() => goTo("classes")}>
            View all classes
          </button>
        </p>
        <ClassTable classes={recent} />
      </div>
    </>
  );
};

const ProfileView = ({ student }) => {
  const rows = [
    ["Student ID", student.studentId],
    ["Full name", student.name],
    ["Email", student.email],
    ["Phone number", student.phoneNumber],
    ["Date of birth", /^\d{4}-\d{2}-\d{2}$/.test(student.dateOfBirth || "") ? longDate(student.dateOfBirth) : student.dateOfBirth],
    ["Program", student.program],
    ["Batch joined", student.batchJoined],
    ["Class type", student.classType],
    ["Local church", student.localChurch],
    ["Address", student.address],
    ["City", student.city],
    ["Province", student.province],
    ["Postal code", student.postalCode],
  ];
  return (
    <>
      <div className="sp-card">
        <p className="sp-card-title">Personal information</p>
        <dl className="sp-profile" style={{ margin: 0 }}>
          {rows.map(([label, value]) => (
            <div key={label}>
              <dt>{label}</dt>
              <dd>{value || <span className="sp-dim">—</span>}</dd>
            </div>
          ))}
        </dl>
        <p className="sp-dim" style={{ margin: "18px 0 0", fontSize: "0.8rem" }}>
          If any of this information is incorrect, please contact the academy administration.
        </p>
      </div>
      <PackageQrCard />
    </>
  );
};

const HEADINGS = {
  dashboard: (data) => [`Welcome, ${data.student.name}`, longDate(data.today)],
  classes: () => [
    "My Classes",
    "Status shows your attendance result for each class. Select a class to see the details.",
  ],
  catchup: () => [
    "Catchup Class",
    "Watch the recording of a session you missed. Your attendance is updated once you have watched enough of it.",
  ],
  profile: () => ["Profile", "Your personal and enrolment information."],
};

// Satu komponen untuk semua halaman (/portal/dashboard, /classes, /catchup, /profile):
// router memakai path "/portal/:view", jadi berpindah menu tidak me-mount
// ulang komponen ini dan datanya cukup diambil sekali.
const StudentPortal = () => {
  const navigate = useNavigate();
  const { view } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  // Dinaikkan setelah absensi diperbaiki lewat kelas susulan, supaya angka
  // kehadiran di Dashboard dan My Classes ikut diperbarui.
  const [reloadKey, setReloadKey] = useState(0);
  const reloadOverview = useCallback(() => setReloadKey((k) => k + 1), []);
  // Sidebar geser di ponsel/tablet (dibuka lewat tombol hamburger).
  const [menuOpen, setMenuOpen] = useState(false);

  // Tutup menu saat pindah halaman.
  useEffect(() => {
    setMenuOpen(false);
  }, [view]);

  // Selama menu terbuka: halaman di belakang tidak ikut ter-scroll, tombol
  // Esc menutup menu, dan menu otomatis tertutup kalau layar dilebarkan
  // sampai sidebar biasa tampil lagi.
  useEffect(() => {
    if (!menuOpen) return undefined;
    const { body, documentElement: html } = document;
    const prev = { body: body.style.overflow, html: html.style.overflow };
    body.style.overflow = "hidden";
    html.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape") setMenuOpen(false);
    };
    const wide = window.matchMedia("(min-width: 861px)");
    const onWide = (e) => {
      if (e.matches) setMenuOpen(false);
    };
    document.addEventListener("keydown", onKey);
    wide.addEventListener ? wide.addEventListener("change", onWide) : wide.addListener(onWide);
    return () => {
      body.style.overflow = prev.body;
      html.style.overflow = prev.html;
      document.removeEventListener("keydown", onKey);
      wide.removeEventListener ? wide.removeEventListener("change", onWide) : wide.removeListener(onWide);
    };
  }, [menuOpen]);

  useEffect(() => {
    let cancelled = false;
    axios
      .get("/studentportal/overview")
      .then((res) => {
        if (!cancelled) setData(res.data);
      })
      .catch((err) => {
        if (cancelled) return;
        if (err.response?.status === 401) navigate("/portal/login", { replace: true });
        else setError(err.response?.data?.msg || "Unable to load your data. Please try again later.");
      });
    return () => {
      cancelled = true;
    };
  }, [navigate, reloadKey]);

  const logout = async () => {
    const result = await Swal.fire({
      title: "Logout",
      text: "Are you sure you want to log out?",
      icon: "question",
      background: "white",
      color: "black",
      showCancelButton: true,
      confirmButtonColor: "#e03a12",
      cancelButtonColor: "#77737c",
      confirmButtonText: "Logout",
    });
    if (!result.isConfirmed) return;
    try {
      await axios.delete("/studentportal/logout");
    } catch (err) {
      // Session yang sudah habis tetap berakhir di halaman login.
    }
    navigate("/portal/login");
  };

  if (!VIEWS.some((v) => v.key === view)) return <Navigate to="/portal/dashboard" replace />;

  const [title, subtitle] = data ? HEADINGS[view](data) : ["Student Portal", ""];

  return (
    <div className="sp-app">
      <div className="sp-topbar">
        <button
          type="button"
          className="sp-menu-btn"
          aria-label="Open menu"
          aria-expanded={menuOpen}
          aria-controls="sp-side-menu"
          onClick={() => setMenuOpen(true)}
        >
          <i className="bi bi-list" aria-hidden="true"></i>
        </button>
        <div className="sp-topbar-brand">
          <img src={WhiteLogo} alt="" />
          <div>
            <strong>CUTTING EDGE ACADEMY</strong>
            <small>Student Portal</small>
          </div>
        </div>
      </div>

      {menuOpen && <div className="sp-drawer-backdrop" onClick={() => setMenuOpen(false)} aria-hidden="true" />}

      <aside id="sp-side-menu" className={`sp-side${menuOpen ? " is-open" : ""}`}>
        <div className="sp-side-brand">
          <img src={WhiteLogo} alt="" />
          <div>
            <strong>CUTTING EDGE ACADEMY</strong>
            <small>Student Portal</small>
          </div>
          <button type="button" className="sp-drawer-close" aria-label="Close menu" onClick={() => setMenuOpen(false)}>
            <i className="bi bi-x-lg" aria-hidden="true"></i>
          </button>
        </div>
        <nav className="sp-nav">
          {VIEWS.map((v) => (
            <button
              key={v.key}
              type="button"
              className={view === v.key ? "is-active" : ""}
              onClick={() => navigate(`/portal/${v.key}`)}
            >
              <i className={`bi ${v.icon}`}></i>
              {v.label}
            </button>
          ))}
        </nav>
        <button type="button" className="sp-logout" onClick={logout}>
          <i className="bi bi-box-arrow-right"></i>
          Logout
        </button>
      </aside>

      <main className="sp-main">
        {error ? (
          <div className="sp-state">{error}</div>
        ) : !data ? (
          <div className="sp-state">Loading your data...</div>
        ) : (
          <>
            <header className="sp-head">
              <div>
                <h1>{title}</h1>
                <p>{subtitle}</p>
              </div>
              <div className="sp-id-chip">
                Student ID <b>{data.student.studentId}</b>
              </div>
            </header>

            {view === "dashboard" && <DashboardView data={data} goTo={(v) => navigate(`/portal/${v}`)} />}
            {view === "classes" && (
              <div className="sp-card">
                <p className="sp-card-title">Class history</p>
                <ClassTable classes={data.classes} expandable onChanged={reloadOverview} />
              </div>
            )}
            {view === "catchup" && <CatchupClass onAttendanceChanged={reloadOverview} />}
            {view === "profile" && <ProfileView student={data.student} />}
          </>
        )}
      </main>
    </div>
  );
};

export default StudentPortal;
